import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Contest, ContestRecipient } from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { ListSkeleton } from '@/features/ui/skeleton';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/concours/concours.css';

const ALPHA2: Record<string, string> = {
  CIV: 'CI',
  GHA: 'GH',
  SEN: 'SN',
  BEN: 'BJ',
  BFA: 'BF',
  MLI: 'ML',
  NER: 'NE',
  TGO: 'TG',
  CMR: 'CM',
  GAB: 'GA',
  COD: 'CD',
  COG: 'CG',
  FRA: 'FR',
  BEL: 'BE',
  CAN: 'CA',
  USA: 'US',
  NGA: 'NG',
  MAR: 'MA',
  TUN: 'TN',
};

function flagEmoji(countryCode: string): string {
  const raw = (countryCode || '').trim().toUpperCase();
  const a2 = raw.length === 2 ? raw : ALPHA2[raw] ?? '';
  if (!/^[A-Z]{2}$/.test(a2)) return '🏳️';
  return String.fromCodePoint(...[...a2].map((c) => 127397 + c.charCodeAt(0)));
}

export function ConcoursHubPage() {
  const toast = useToast();
  const [contests, setContests] = useState<Contest[]>([]);
  const [selectedContestId, setSelectedContestId] = useState('');
  const [recipients, setRecipients] = useState<ContestRecipient[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  const selectedContest = useMemo(
    () => contests.find((c) => c.id === selectedContestId) ?? null,
    [contests, selectedContestId],
  );

  useEffect(() => {
    void (async () => {
      const { data, error: err } = await kuunda
        .from('contests')
        .select('id, code, name, is_active, challenge_year, documentation_url, documentation_label')
        .order('challenge_year', { ascending: false });
      if (err) {
        toast.error(err.message);
        return;
      }
      const list = (data ?? []) as Contest[];
      const withYear = list.filter((c) => c.challenge_year != null);
      const challenges = (withYear.length > 0 ? withYear : list).sort((a, b) => {
        const ya = a.challenge_year ?? 0;
        const yb = b.challenge_year ?? 0;
        return yb - ya;
      });
      setContests(challenges);
      const preferred =
        challenges.find((c) => c.is_active) ??
        challenges.find((c) => c.challenge_year === 2026) ??
        challenges[0];
      if (preferred) setSelectedContestId(preferred.id);
    })();
  }, []);

  useEffect(() => {
    if (!selectedContestId) {
      setRecipients([]);
      return;
    }
    void (async () => {
      setLoadingRecipients(true);
      const { data, error: err } = await kuunda
        .from('contest_recipients')
        .select(
          'id, contest_id, project_name, bearer_name, entrepreneurial_solution, country_code, country_name, sort_order',
        )
        .eq('contest_id', selectedContestId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
      setLoadingRecipients(false);
      if (err) {
        toast.error(err.message);
        setRecipients([]);
        return;
      }
      setRecipients((data ?? []) as ContestRecipient[]);
    })();
  }, [selectedContestId]);

  return (
    <div className="u-page">
      <div className="cx-banner">
        <div className="cx-banner-icon" aria-hidden>
          🏆
        </div>
        <div>
          <h3>Concours Excellence Entrepreneurial</h3>
          <p>Propulsez votre projet entrepreneurial vers le succès</p>
        </div>
      </div>

      <div className="cx-layout">
        <div>
          <div className="cx-challenge-select">
            <span aria-hidden>🚀</span>
            <label className="cx-sr-only" htmlFor="cx-challenge">
              Sélectionner un challenge
            </label>
            <select
              id="cx-challenge"
              value={selectedContestId}
              onChange={(e) => setSelectedContestId(e.target.value)}
            >
              {contests.length === 0 ? (
                <option value="">Aucun challenge</option>
              ) : (
                contests.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.challenge_year ? `Challenge ${c.challenge_year}` : c.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <section className="u-card cx-recipients">
            <div className="cx-recipients-head">
              <span aria-hidden>🏅</span>
              <h3>Liste des récipiendaires</h3>
            </div>
            <div className="cx-recipients-body">
              {loadingRecipients ? (
                <div style={{ margin: '0.75rem' }}>
                  <ListSkeleton rows={3} />
                </div>
              ) : recipients.length === 0 ? (
                <p className="u-empty" style={{ margin: '0.75rem' }}>
                  Aucun récipiendaire publié pour{' '}
                  {selectedContest?.challenge_year
                    ? `le Challenge ${selectedContest.challenge_year}`
                    : 'ce challenge'}
                  .
                </p>
              ) : (
                <>
                  <div className="cx-recipients-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Liste projets</th>
                          <th>Porteur</th>
                          <th>Solution entrepreneuriale</th>
                          <th>Pays</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipients.map((r) => (
                          <tr key={r.id}>
                            <td>
                              <strong>{r.project_name}</strong>
                            </td>
                            <td>{r.bearer_name}</td>
                            <td>{r.entrepreneurial_solution || '—'}</td>
                            <td>
                              <span className="cx-country">
                                <span aria-hidden>{flagEmoji(r.country_code)}</span>
                                {r.country_code || r.country_name || '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="cx-recipients-list" aria-hidden="false">
                    {recipients.map((r) => (
                      <article key={r.id} className="cx-recipient-card">
                        <strong>{r.project_name}</strong>
                        <p>
                          <span className="muted">Porteur · </span>
                          {r.bearer_name}
                        </p>
                        <p>{r.entrepreneurial_solution || '—'}</p>
                        <p className="cx-country">
                          <span aria-hidden>{flagEmoji(r.country_code)}</span>
                          {r.country_code || r.country_name || '—'}
                        </p>
                      </article>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="cx-recipients-foot">
              <span aria-hidden>↻</span>
              Les récipiendaires s&apos;actualisent selon le challenge choisi
            </div>
          </section>
        </div>

        <aside className="cx-side">
          <Link to="/app/concours/inscription" className="btn btn-primary cx-side-btn">
            <span aria-hidden>✎</span>
            S&apos;inscrire aux concours
          </Link>
          <Link to="/app/concours/evolution" className="btn cx-side-btn is-track">
            <span aria-hidden>📈</span>
            Suivre mon évolution
          </Link>
          <div className="cx-docs">
            <div className="cx-docs-icon" aria-hidden>
              📄
            </div>
            <h4>Documentation concours</h4>
            <p>Base de documents : règlements, guides, modèles et critères de sélection.</p>
            <Link to="/app/concours/documentation" className="btn btn-primary">
              Accéder à la base
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
